import { startTransition, useEffect, useState } from "react";

/**
 * from https://github.com/robinjonsson/react-use-sticky
 */
export function useSticky<T extends HTMLElement>(
  stickyRef: React.RefObject<T | undefined>,
  offset?: number
): boolean {
  const [sticky, setSticky] = useState(false);

  useEffect(() => {
    function observe() {
      if (!stickyRef.current) return;
      const refPageOffset = stickyRef.current.getBoundingClientRect().top;
      const stickyOffset =
        parseInt(getComputedStyle(stickyRef.current).top) + (offset || 0);

      const stickyActive = refPageOffset <= stickyOffset;

      if (stickyActive && !sticky) startTransition(() => setSticky(true));
      else if (!stickyActive && sticky) startTransition(() => setSticky(false));
    }
    observe();

    // Bind events
    document.addEventListener("scroll", observe);
    window.addEventListener("resize", observe);
    window.addEventListener("orientationchange", observe);

    return () => {
      document.removeEventListener("scroll", observe);
      window.removeEventListener("resize", observe);
      window.removeEventListener("orientationchange", observe);
    };
  }, [offset, sticky, stickyRef]);

  return sticky;
}

export function useBottomSticky<T extends HTMLElement>(
  stickyRef: React.RefObject<T | undefined>
): boolean {
  const [sticky, setSticky] = useState(false);

  useEffect(() => {
    function observe() {
      if (!stickyRef.current) return;
      const { top } = stickyRef.current.getBoundingClientRect();

      const windowHeight = window.innerHeight;
      const elementHeight = stickyRef.current.clientHeight;

      const delta = windowHeight - elementHeight - 1;
      const stickyActive = delta < top;

      if (stickyActive && !sticky) startTransition(() => setSticky(true));
      else if (!stickyActive && sticky) startTransition(() => setSticky(false));
    }
    observe();

    // Bind events
    document.addEventListener("scroll", observe);
    window.addEventListener("resize", observe);
    window.addEventListener("orientationchange", observe);

    return () => {
      document.removeEventListener("scroll", observe);
      window.removeEventListener("resize", observe);
      window.removeEventListener("orientationchange", observe);
    };
  }, [sticky, stickyRef]);

  return sticky;
}

export function useMenuSticky(offset?: number): boolean {
  const [sticky, setSticky] = useState(false);

  useEffect(() => {
    const headerElement =
      document.querySelector<HTMLElement>("[data-main-header]");
    function observe() {
      if (!headerElement) return;
      const refPageOffset = window.scrollY;
      const stickyOffset = offset || 0;
      const stickyActive = refPageOffset > stickyOffset;
      if (stickyActive && !sticky) startTransition(() => setSticky(true));
      else if (!stickyActive && sticky) startTransition(() => setSticky(false));
    }
    observe();

    // Bind events
    document.addEventListener("scroll", observe);
    window.addEventListener("resize", observe);
    window.addEventListener("orientationchange", observe);

    return () => {
      document.removeEventListener("scroll", observe);
      window.removeEventListener("resize", observe);
      window.removeEventListener("orientationchange", observe);
    };
  }, [offset, sticky]);

  return sticky;
}
